import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShoppingBag } from "lucide-react";
import { routes } from "@/consts";
import { LANG } from "@/types";
// import useProducts from "@/hooks/use-products";
import { Card, CardContent } from "@/app/global-components/card";
import { Button } from "@/app/global-components/button";
import { BiHeart } from "react-icons/bi";
import { FaStore } from "react-icons/fa";
import { TbTargetArrow } from "react-icons/tb";
import { BiBasket } from "react-icons/bi";
import { BsFillBasketFill } from "react-icons/bs";
import { useCart } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import useItems from "@/hooks/use-item";

interface Product {
  id: string;
  companyId: string;
  categories: string[];
  name: string;
  store: {
    name: string;
    city?: string;
    region?: string;
  };
  price: number;
  minOrder: number;
  unit: string;
  images: string[];
  isFavorite?: boolean;
  quantity?: number;
}

const MarketplaceSection: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { allMarketplaceProduct } = useItems();
  const navigate = useNavigate();
  const {
    addToCart,
    removeFromCart,
    cartItems,
    updateQuantity,
    addToInquiry,
    removeFromInquiry,
    isInInquiry,
  } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const result = await allMarketplaceProduct({});
        if (result && result.length > 0) {
          // Take only the first 6 products
          setProducts(result.slice(0, 6));
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
    const [quantity, setQuantity] = useState(
      product.minOrder === 0 ? 1 : product.minOrder
    );
    const isInCart = cartItems.some((item) => item.id === product.id);
    const cartItem = cartItems.find((item) => item.id === product.id);
    const inInquiry = isInInquiry(product.id);

    useEffect(() => {
      if (cartItem) {
        setQuantity(cartItem.quantity);
      } else {
        setQuantity(product.minOrder === 0 ? 1 : product.minOrder);
      }
    }, [cartItem, product.minOrder]);

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value.split("")[0] === "0") {
        setQuantity(parseInt(e.target.value.split("")[1]));
        return;
      }
      const value = parseInt(e.target.value);
      if (!isNaN(value) && value >= product.minOrder) {
        setQuantity(value);
        if (isInCart) {
          updateQuantity(product.id, value);
        }
      }
    };

    const handleIncrement = (e: React.MouseEvent) => {
      e.stopPropagation();
      const newQuantity = quantity + 1;
      setQuantity(newQuantity);
      if (isInCart) {
        updateQuantity(product.id, newQuantity);
      }
    };

    const handleDecrement = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (quantity <= product.minOrder || isInCart) {
        return null;
      }
      if (quantity > product.minOrder) {
        const newQuantity = quantity - 1;
        setQuantity(newQuantity);
        if (isInCart) {
          updateQuantity(product.id, newQuantity);
        }
      }
    };

    const handleCartToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isInCart) {
        removeFromCart(product.id);
        setQuantity(product.minOrder);
        toast.warning("Product removed from cart", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
      } else {
        const productWithQuantity = {
          ...product,
          quantity: Number(quantity),
        };
        addToCart(productWithQuantity);
        toast.success(t("buyer_pages.product_details.added"), {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
      }
    };

    const handleInquiryToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (inInquiry) {
        removeFromInquiry(product.id);
      } else {
        const productWithQuantity = {
          ...product,
          companyId: product.companyId,
          categories: product.categories,
          quantity: product.quantity || product.minOrder,
        };
        addToInquiry(productWithQuantity);
      }
    };

    const handleCardClick = () => {
      navigate(`/product/${product.id}`);
    };

    const sliderSettings = {
      dots: true,
      infinite: true,
      speed: 1000,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4000,
    };

    const renderImages = () => {
      if (product.images.length === 1) {
        return (
          <div className="relative overflow-hidden rounded-t-xl">
            <img
              src={product.images[0]}
              alt={`${product?.name} - Image 1`}
              className="w-full h-full object-cover transition-transform duration-300"
            />
            <div className="absolute inset-0 transition-opacity duration-300" />
          </div>
        );
      }

      return (
        <div className="relative overflow-hidden rounded-t-xl">
          <Slider arrows={false} {...sliderSettings}>
            {product.images.map((image, index) => (
              <div key={image} className="bg-white aspect-[4/3]">
                <img
                  src={image}
                  alt={`${product?.name} - Image ${index + 1}`}
                  className="w-full h-full object-contain transition-transform duration-300"
                />
              </div>
            ))}
          </Slider>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
        </div>
      );
    };

    return (
      <Card
        className="overflow-hidden w-full bg-white cursor-pointer transition-all duration-300 h-full border-0 rounded-2xl group"
        onClick={handleCardClick}
      >
        <div className="relative">
          {renderImages()}
          <div className="absolute top-3 right-3 flex flex-col gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full bg-white/90 hover:bg-white backdrop-blur-sm transition-all duration-200"
            >
              <BiHeart className="w-4 h-4 text-gray-500" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 rounded-full backdrop-blur-sm transition-all duration-200 ${
                isInCart
                  ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                  : "bg-white/90 hover:bg-white text-gray-500"
              }`}
              onClick={handleCartToggle}
            >
              {isInCart ? (
                <BsFillBasketFill className="w-4 h-4" />
              ) : (
                <BiBasket className="w-4 h-4" />
              )}
            </Button>
            <Button
              size="icon"
              className={`h-8 w-8 rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 ${
                inInquiry
                  ? "bg-green-500 hover:bg-green-600 text-white"
                  : "bg-white/90 hover:bg-white text-gray-500"
              }`}
              onClick={handleInquiryToggle}
            >
              <TbTargetArrow className="w-4 h-4" />
            </Button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 to-transparent p-3">
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-1 px-2 py-1 bg-white/90 rounded-full backdrop-blur-sm">
                <FaStore className="text-blue-600 w-3 h-3" />
                <span className="text-xs text-blue-700 font-medium">
                  {product.store?.name}
                </span>
              </div>
            </div>
          </div>
        </div>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-2 text-gray-800 line-clamp-2 h-10">
            {product?.name}
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-lg font-bold text-emerald-600 mb-2">
                {product.price.toFixed(2)} {t("currency")}/{product.unit}
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  disabled={
                    quantity <=
                      (product.minOrder === 0 ? 1 : product.minOrder) ||
                    isInCart
                  }
                  className="h-8 w-8 rounded-lg border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all duration-200"
                  onClick={handleDecrement}
                >
                  -
                </Button>
                <input
                  type="number"
                  min={product.minOrder === 0 ? 1 : product.minOrder}
                  value={quantity}
                  onClick={(e) => e.stopPropagation()}
                  onChange={handleQuantityChange}
                  className="w-16 h-8 text-xs text-center border border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-emerald-500"
                  disabled={isInCart}
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-lg border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all duration-200"
                  onClick={handleIncrement}
                  disabled={isInCart}
                >
                  +
                </Button>
                <span className="text-xs text-gray-500">{product.unit}s</span>
              </div>
            </div>
            <div className="pt-2 border-t border-gray-100">
              <div className="text-sm font-bold">
                {t("buyer_pages.cart.total")}:{" "}
                {(product.price * quantity).toFixed(2)} {t("currency")}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              {t("marketplace.title")}
            </h2>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
            {t("marketplace.subtitle")}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-blue-500 mx-auto rounded-full"></div>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center items-center py-16"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-lg text-gray-600">
                {t("marketplace.loadingProducts")}
              </span>
            </div>
          </motion.div>
        )}

        {/* Products Grid */}
        {!loading && products.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8 mb-12"
          >
            {products.map((product) => (
              <motion.div key={product.id} variants={itemVariants}>
                <ProductCard product={product} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && products.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t("marketplace.noProductsAvailable")}
              </h3>
              <p className="text-gray-600">
                {t("marketplace.noProductsDescription")}
              </p>
            </div>
          </motion.div>
        )}

        {/* View All Products Button */}
        {!loading && products.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to={routes.marketPlace}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-blue-500 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg transition-all duration-300 shadow-md"
              >
                <span>{t("actions.viewAllProducts")}</span>
                {locale === LANG.EN ? (
                  <ArrowRight className="w-5 h-5" />
                ) : (
                  <ArrowLeft className="w-5 h-5" />
                )}
              </motion.button>
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default MarketplaceSection;
